import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { StatsigClient, StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
//import { StatsigProvider } from "statsig-react";

import showdown from "showdown";
import styled from "styled-components";
import { Model, Serializer } from "survey-core";
import {
  AfterRenderPageEvent,
  CompletingEvent,
  CurrentPageChangedEvent,
  TextMarkdownEvent,
} from "survey-events-api";
import { Survey } from "survey-react-ui";

import { BlackFridayTimer } from "./features/BlackFridayTimer";
import { AccountExistsScreen } from "./features/AccountExistsScreen";
import ProgressHeader from "./features/ProgressHeader";
import { LandingPage } from "./LandingPage";
import {
  COMPONENT_CONFIG,
  isTransitionComponentKey,
  makeQuizNav,
  QuizSection,
  SURVEY_JSON,
} from "./quiz";
import type { QuizNav } from "./quiz";
import { buildCampaignSurveyJson } from "./quiz.config";
import { Goals } from "./quiz/goals";
import {
  FunnelVariation,
  pushToDataLayer,
  QuizEventData,
  sendChangesToGTM,
  sendPageViewEvent,
} from "./services/googleTagManager";
import { submitUserToHubspot } from "./services/hubspot";
import {
  accountExists,
  getAccountExistsCheckForEmail,
  getAccountExistsEmailKey,
} from "./services/account";
import type { AccountExistsCheck } from "./services/account";
import { User } from "./types";

import * as amplitude from "@amplitude/analytics-browser";
import Cookies from "js-cookie";
import { Route, Routes, useLocation, useMatch, useNavigate } from "react-router-dom";
import './App.scss';
import { getCdnImageUrl } from "./features/images";
import {
  getProgramNameForUser,
  LANDING_PAGE_CHOICES,
} from "./features/programs/Program.config";
import { getVersionNumber, STATSIG_CLIENT_KEY } from "./services/experiments";
// @ts-ignore
import { Identify } from '@amplitude/analytics-browser';

import { CheckoutPage } from './CheckoutPage';
import { ReferralBanner } from './features/ReferralBanner';
import { isReferralLink, useReferral } from './referral';
import { ReservationPage } from './features/reservation/ReservationPage';
import { ReservationComingSoon } from './features/reservation/ReservationComingSoon';

import { v4 as uuidv4 } from "uuid";
import QuestionFooter from "./features/QuestionFooter";
import QuestionHeader from "./features/QuestionHeader";
import BridgeScreen from "./features/BridgeScreen";
import { BRIDGE_EXPERIMENT_NAME, buildBridgeControlRedirectUrl } from "./features/bridgeExperiment";
import {
  fetchBridges,
  getBridgeBlocks,
  getBridgeRoute,
  getControlUrl,
  getIntroSequence,
  getStableBucketId,
  isInBridgeSplit,
  shouldShowBridge,
} from "./features/bridgeCms";

// Import custom carousel question type
import axios from "axios";
import { DevTool } from "./features/DevTool";
import { useAnswerTracker } from "./hooks/useAnswerTracker";
import "./quiz/carousel";
import {
  buildOpeningArcPages,
  isWeb39Eligible,
  OPENING_ARC_ORGANIC_DEFAULT,
  WEB39_ARM_VARIATION,
  WEB39_EXPERIMENT_NAME,
} from "./quiz/web33";
import {
  buildScaleQuestionPages,
  WEB32_SCALE_ARM_VARIATION,
  WEB32_SCALE_EXPERIMENT_NAME,
} from "./quiz/web32scale";
import {
  buildHsaQuestionPages,
  WEB38_HSA_ARM_VARIATION,
  WEB38_HSA_EXPERIMENT_NAME,
  WEB38_HSA_QUESTION_NAME,
} from "./quiz/web38hsa";
import { getAssignedExperimentArm, getForcedExperimentArm, getUTMParams, getWeb43Arm, getWeb46Arm, trackWeb46Exposure, getWeb48Arm, getWeb48EndsAt, trackWeb48Exposure, isPaidAdTraffic, WEB43_EXPERIMENT_NAME } from "./utils";
import { Web48Banner } from "./features/web48/Web48Banner";
import { writeWeb48Offer } from "./web48Offer";
import { getBfSale, toSalePhaseTag, writeStoredBfSale } from "./bfSale";
import { getBfBannerDesign } from "./utils";
import { setSalePhase } from "./services/salePhaseTracking";
import {
  buildGymBuddyClarityPages,
  GUEST_PASS_PAGE,
  GYM_BUDDY_ENTRY_PAGE,
  isGymBuddyOptedIn,
  PAGE_AFTER_GUEST_PASS,
  WEB43_ARM_VARIATION,
} from "./quiz/web43gymBuddy";
import {
  getCapturedTrackingParams,
  persistTrackingParams,
} from "./trackingParams";
import { trackCustom } from "./services/facebookPixel";
import { trackQuizCompleted, trackQuizStarted } from "./services/googleTagManager";
import { useTranslation } from "react-i18next";
import type { QuizLanguage } from "./i18n";
import { applyQuizLanguage, applySurveyLanguage, resolveQuizLanguage, saveLanguageChoice } from "./i18n/language";
import { QuizLanguageContext } from "./i18n/LanguageSwitch";
import { pickLoc } from "./i18n/survey";
import i18n from "./i18n";
import { Project60StickyBanner } from './Project60StickyBanner';
import { Project60StickyBanner as Quiz4UrgencyBanner } from './features/Quiz4UrgencyBanner';
import {
  applyCheckoutTestModeOverride,
  CHECKOUT_V2_STORAGE_KEY,
  keepForcedCheckoutTestModeEnabled,
} from "./checkoutTestMode";

let vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty('--vh', `${vh}px`);
window.addEventListener('resize', () => {
  // We execute the same script as before
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
});
applyCheckoutTestModeOverride(window.location.search);

// A shared referral link is a new quiz entry. Subsequent /page/... reloads
// still resume normally, and unrelated attribution parameters stay intact.
if (window.location.pathname === "/" && isReferralLink(window.location.search)) {
  try {
    localStorage.removeItem("BWS_SURVEY_DATA");
    localStorage.removeItem("BWS_LAST_UPDATED");
  } catch { /* Referral storage must not prevent a visitor from starting. */ }
}

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || '';

// sorted somewhat in the order we need them
const IMAGE_PATHS = [
  'images/bodyfat/female35.jpg',
  'images/bodyfat/female20.jpg',
  'images/bodyfat/male40.jpg',
  'images/bodyfat/female30.jpg',
  'images/bodyfat/female18.jpg',
  'images/bodyfat/female25.jpg',
  'images/bodyfat/goalMale10.jpg',
  'images/bodyfat/male20.jpg',
  'images/bodyfat/male35.jpg',
  'images/bodyfat/female40.jpg',
  'images/bodyfat/goalMale12.jpg',
  'images/bodyfat/female45.jpg',
  'images/bodyfat/goalMale15.jpg',
  'images/bodyfat/male25.jpg',
  'images/bodyfat/male30.jpg',
  'images/bodyfat/male15.jpg',
  'images/bodyfat/male12.jpg',
  'images/bodyfat/goalMale20.jpg',
  'images/bodyfat/male10.jpg',
  'images/bodyfat/female15.jpg',
  'images/bodytype/Male/overweight.jpg',
  'images/bodytype/Male/skinny.png',
  'images/bodytype/Male/bulky.jpg',
  'images/bodytype/Male/skinnyfat.png',
  'images/bodytype/Female/overweight.jpg',
  'images/bodytype/Female/skinny.jpg',
  'images/bodytype/Female/bulky.png',
  'images/bodytype/Female/skinnyfat.jpg',
  'images/bws-loading-chart-2.jpg',
  'images/ktla.webp',
  'images/womensFitness.jpg',
  'images/splash/timeMale.jpg',
  'images/splash/protein.jpg',
  'images/splash/goodHands.jpg',
  'images/splash/timeFemale.jpg',
  'images/splash/habit.jpg',
  'images/splash/injuriesFemale.jpg',
  'images/splash/injuriesMale.jpg',
  'images/splash/busyLevel.jpg',
  'images/testimonials/brian.jpg',
  'images/testimonials/brian.png',
  'images/testimonials/jade.png',
  'images/testimonials/jade.jpg',
  'images/testimonials/cesar.png',
  'images/testimonials/cesar.jpg',
  'images/testimonials/katy.jpg',
  'images/testimonials/stephen.jpg',
  'images/testimonials/jesse.jpg',
  'images/testimonials/patrick.jpg',
  'images/testimonials/abdullah.jpg',
  'images/testimonials/salma.jpg',
  'images/testimonials/salma.png',
  'images/testimonials/ligia.jpg',
  'images/testimonials/adam.png',
  'images/testimonials/adam.jpg',
  'images/testimonials/reinis.jpg',
  'images/testimonials/tahnee.jpg',
  'images/testimonials/jan.jpg',
  'images/testimonials/jan.png',
  'images/youtube_social_icon_white.png',
  'images/TikTok_Icon_Black_Circle.png',
  'images/yahoo.jpg',
  'images/landingPage/FemaleStrongPremium.jpg',
  'images/landingPage/MaleBuildPremium.jpg',
  'images/landingPage/FemaleStrongBase.jpg',
  'images/landingPage/FemaleLeanElite.jpg',
  'images/landingPage/MaleBuildElite.jpg',
  'images/landingPage/MaleShredElite.jpg',
  'images/landingPage/FemaleLeanBase.jpg',
  'images/landingPage/MaleBuildBase.jpg',
  'images/landingPage/MaleShredPremium.jpg',
  'images/landingPage/FemaleLeanPremium.jpg',
  'images/landingPage/MaleShredBase.jpg',
  'images/landingPage/FemaleStrongElite.jpg',
  'images/f_logo_RGB-Blue_144.png',
  'images/mensHealth.jpg',
  'images/bws-loading-chart.jpg',
  'images/bws-images-basic/basic-build-intermediate.png',
  'images/bws-images-basic/basic-build.png',
  'images/bws-images-basic/basic-lean-intermediate.png',
  'images/bws-images-basic/basic-lean.png',
  'images/bws-images-basic/basic-shred-intermediate.png',
  'images/bws-images-basic/basic-shred.png',
  'images/bws-images-basic/basic-strong-intermediate.png',
  'images/bws-images-basic/basic-strong.png',
  'images/bws-images-basic/BWS%2BElite+Female.jpg',
  'images/bws-images-basic/BWS%2BElite+Male.jpg',
  'images/bws-images-basic/BWS%2BPremium+Female.jpg',
  'images/bws-images-basic/BWS%2BPremium+Male.jpg',
  'images/new-quiz-bodytype-images-2025/male-bodytype/1.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/2.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/3.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/4.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/5.png',
  'images/new-quiz-bodytype-images-2025/male-bodytype/6.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/1.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/2.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/3.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/4.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/5.png',
  'images/new-quiz-bodytype-images-2025/female-bodytype/6.png',


  'images/new-quiz-bodyfat-images/Male/10.png',
  'images/new-quiz-bodyfat-images/Male/12.png',
  'images/new-quiz-bodyfat-images/Male/15.png',
  'images/new-quiz-bodyfat-images/Male/18.png',
  'images/new-quiz-bodyfat-images/Male/20.png',
  'images/new-quiz-bodyfat-images/Male/40.png',
  'images/new-quiz-bodyfat-images/Male/22.png',
  'images/new-quiz-bodyfat-images/Male/25.png',
  'images/new-quiz-bodyfat-images/Male/28.png',
  'images/new-quiz-bodyfat-images/Male/30.png',
  'images/new-quiz-bodyfat-images/Male/32.png',
  'images/new-quiz-bodyfat-images/Male/35.png',
  'images/new-quiz-bodyfat-images/Female/18.png',
  'images/new-quiz-bodyfat-images/Female/20.png',
  'images/new-quiz-bodyfat-images/Female/22.png',
  'images/new-quiz-bodyfat-images/Female/25.png',
  'images/new-quiz-bodyfat-images/Female/28.png',
  'images/new-quiz-bodyfat-images/Female/30.png',
  'images/new-quiz-bodyfat-images/Female/32.png',
  'images/new-quiz-bodyfat-images/Female/35.png',
  'images/new-quiz-bodyfat-images/Female/38.png',
  'images/new-quiz-bodyfat-images/Female/40.png',
  'images/new-quiz-bodyfat-images/Female/42.png',
  'images/new-quiz-bodyfat-images/Female/45.png',
  'images/new-quiz-bodyfat-images/Female/50.png',
  'images/new-quiz-bodyfat-images/Female/15.png',
];

const AppContainer = styled.div`
  font-family: 'Lato', sans-serif;

  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);

  display: flex;
  flex-direction: column;
  &:has(.quote-container) {
    background-color: #add8e6;
  }
  &:has(.new-quote-container),
  &:has(.web33-reveal),
  &:has(.web33-path-splash) {
    background: linear-gradient(180deg, #E7F0FF 10.59%, #FFFFFF 86.15%);
    .sd-root-modern {
      background: transparent;
      display: flex;
      justify-content: center;
    }
  }

  &:has(.new-quote-container.centered) {
    .sd-root-modern {
      align-items: center;
    }
  } 
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

// web46 personalization / web48: holds the pricing page's sticky banner
// (fixed bar plus its in-flow spacer) at the top of every quiz page.
const QuizMatchBannerSlot = styled.div`
  flex-shrink: 0;
`;

export const LOCAL_STORAGE_KEY = 'BWS_SURVEY_DATA';
const LAST_UPDATED_KEY = 'BWS_LAST_UPDATED';
const SEVEN_DAY_IN_MS = 7 * 24 * 60 * 60 * 1000;
const SIGNUP_ACCOUNT_GATE_PATHS = new Set([
  "/payment",
  "/password",
  "/checkout",
  "/results",
]);

const isSignupAccountGatePath = (pathname: string): boolean => {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  return SIGNUP_ACCOUNT_GATE_PATHS.has(normalizedPathname);
};

const getTrimmedEmail = (email: unknown): string | null => {
  if (typeof email !== "string") {
    return null;
  }

  const trimmedEmail = email.trim();
  return trimmedEmail || null;
};

const getStoredSurveyEmail = (): string | null => {
  try {
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsedSurvey = storedSurvey
      ? (JSON.parse(storedSurvey) as { data?: { email?: unknown } })
      : {};

    return getTrimmedEmail(parsedSurvey.data?.email);
  } catch {
    return null;
  }
};

const useImagePreloader = () => {
  useEffect(() => {
    const preloadImages = () => {
      const promises = IMAGE_PATHS.map((path) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = getCdnImageUrl(path);
        });
      });

      Promise.all(promises).catch((error) => {
        console.error('Error preloading images:', error);
      });
    };
    preloadImages();
  }, []);
};

type TransitionComponentProps<T> = {
  user: T;
  survey: Model;
  onComplete: () => void;
  openingArcOn?: boolean;
};

type EmailAccountGateState = {
  status: "idle" | "checking" | "found" | "error";
  email: string | null;
};

const createIdleEmailAccountGateState = (): EmailAccountGateState => ({
  status: "idle",
  email: null,
});

Serializer.addProperty("page", {
  name: "props",
  category: "general",
  default: {},
});

const markdownToHtmlFuncGenerator = (converter: showdown.Converter) => (_: Model, options: TextMarkdownEvent) => {
  const { element, name, text } = options;
  const isSurveyPartToUpdate = ['title', 'description'].includes(name);
  const isSurveyType = element.getType() !== 'survey';
  const allowedHtmlQuestionNames = [
    'goals',
    'describeSelf',
    'experience',
    'science',
    'cardio',
    'comfort',
    'equipment',
    'typeofsupport',
    'activity',
    'mainNutritionHelp',
    'foodTracking',
    'workoutPlan',
    'concerns',
    'transformationExpectation', // opening arc: this question uses SVG icon-button choices.
  ];
  const isHtmlAllowed = allowedHtmlQuestionNames.includes(element.name);
  const isTextName = name === 'text';

  if ((isSurveyPartToUpdate && isSurveyType) || (isHtmlAllowed && isTextName)) {
    const htmlStr = converter.makeHtml(text);
    const startTag = '<p>';
    const endTag = '</p>';
    const startTagIndex = htmlStr.indexOf(startTag);
    const endTagIndex = htmlStr.lastIndexOf(endTag);

    options.html = startTagIndex !== -1 && endTagIndex !== -1 ? htmlStr.slice(startTagIndex + startTag.length, endTagIndex) : htmlStr;
  }
};

const onAfterRenderPageFuncGenerator =
  (setProgressPercent: Dispatch<SetStateAction<number>>, setPageTotal: Dispatch<SetStateAction<number>>, setTitle: Dispatch<SetStateAction<QuizSection>>, nav: QuizNav) =>
  (_: Model, options: AfterRenderPageEvent) => {
    const questionName = options.page.name;
    const section = nav.getQuestionSection(questionName);
    if (!!section) {
      const sectionInfo = nav.getSectionInfo(section);
      setTitle(section);

      // Calculate total questions across all sections
      const totalQuestions = Object.values(QuizSection).reduce((sum, sectionKey) => {
        const sectionTotal = nav.getSectionInfo(sectionKey as QuizSection);
        return sum + sectionTotal.total;
      }, 0);
      setPageTotal(totalQuestions);

      // Calculate progress based on section
      // Each band is clamped to its ceiling: a page resolved via the nav's
      // nearest-section fallback (a transform-inserted page just past a section's
      // end anchor) would otherwise overshoot its band and make the bar dip when
      // the next section starts. In-range pages never exceed the ceiling, so the
      // clamp is a no-op for them.
      let progress = 0;
      if (section === QuizSection.GETTING_STARTED) {
        // First section is 50% of total progress
        progress = Math.min(50, Math.floor((nav.findPageIndex(questionName) - sectionInfo.startIndex + 1) * (50 / sectionInfo.total)));
      } else if (section === QuizSection.HABITS_AND_BEHAVIORS) {
        // Second section is 25% of total progress
        progress = 50 + Math.min(25, Math.floor((nav.findPageIndex(questionName) - sectionInfo.startIndex + 1) * (25 / sectionInfo.total)));
      } else if (section === QuizSection.TRAINING_AND_NUTRITION) {
        // Third section is 25% of total progress
        progress = 75 + Math.min(25, Math.floor((nav.findPageIndex(questionName) - sectionInfo.startIndex + 1) * (25 / sectionInfo.total)));
      }
      setProgressPercent(progress);

    } else {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Missing section configuration for question: ${questionName}`); // TODO send errors to some logging service
      }
    }
  };

// Meta pixel: first real interaction — the user pressed Next on the first quiz screen.
// Fires once per page load (QuizStarted fires on load, so it is not an interaction signal).
let quizNextFired = false;

const onCurrentPageChangedFuncGenerator =
  (config: QuizTransitionConfig, setTransitionComponentKey: Dispatch<SetStateAction<string | null>>, setUser: Dispatch<SetStateAction<User>>, trackAnswer: (questionName: string, answer: any, allAnswers: any) => void, nav: QuizNav) =>
  (sender: Model, options: CurrentPageChangedEvent) => {
    const { oldCurrentPage, newCurrentPage } = options;
    const transitionConfig = config[oldCurrentPage.name];
    if (transitionConfig && transitionConfig.nextPage === newCurrentPage.name) {
      setTransitionComponentKey(oldCurrentPage.name);
    }
    sendChangesToGTM(sender, oldCurrentPage, nav.findPageIndex(oldCurrentPage.name) + 1);
    if (!quizNextFired && !options.isPrevPage && nav.findPageIndex(oldCurrentPage.name) === 0) {
      quizNextFired = true;
      trackCustom("QuizNext", { step_name: oldCurrentPage.name });
    }

    const previousPageName = oldCurrentPage.name;
    const answer = sender.data[previousPageName];
    //trackAnswer(previousPageName, answer, sender.data);

    if (oldCurrentPage.name === 'email') {
      // we only want to submit the user once they have entered their email

      submitUserToHubspot(sender.data, false).then(userId =>{
        const identifyObjUser = new Identify();
      identifyObjUser.set('HubspotUserID',userId);
      amplitude.identify(identifyObjUser);
      });


    }
    setUser((user) => ({
      ...user,
      [oldCurrentPage.name]: sender.data[oldCurrentPage.name],
      ...(oldCurrentPage.name === 'weight'
        ? {
            weightUnit: sender.data.weightUnit,
          }
        : {}),
      ...(oldCurrentPage.name === 'height'
        ? {
            heightUnit: sender.data.heightUnit,
            heightRaw: sender.data.heightRaw,
            heightInches: sender.data.heightInches,
          }
        : {}),
    }));


  };

const onCompletingFuncGenerator =
  (setTransitionComponentKey: Dispatch<SetStateAction<string | null>>, setUser: Dispatch<SetStateAction<User>>, nav: QuizNav) => (sender: Model, _: CompletingEvent) => {
    const pageName: string = sender.currentPage.name;

    setTransitionComponentKey(pageName);

    sendChangesToGTM(sender, sender.currentPage, nav.findPageIndex(sender.currentPage.name) + 1);
    setUser((user) => ({
      ...user,
      [pageName]: sender.data[pageName],
    }));
    if (isTransitionComponentKey(pageName)) {
      const goal = sender.data.goals as Goals;
      sendPageViewEvent(
        goal,
        COMPONENT_CONFIG[pageName].componentName,
        getProgramNameForUser(sender.data),
        LANDING_PAGE_CHOICES,
        nav.findPageIndex(COMPONENT_CONFIG[pageName].componentName) + 1
      );
    }
    if (isTransitionComponentKey(pageName)) {
      submitUserToHubspot(sender.data, true).then(userId =>{
        const identifyObjUser = new Identify();
        identifyObjUser.set('HubspotUserID',userId);
        amplitude.identify(identifyObjUser);
      });
    }
  };

type QuizTransitionConfig = {
  [key: string]: {
    nextPage?: string;
    component?: (props: TransitionComponentProps<User>) => JSX.Element;
  };
};

let didAddCustomAutoAdvanceHandler = false;

function useSurvey(
  surveyJson: Object,
  converter: showdown.Converter,
  statsigClient: StatsigClient,
  isStatsigClientLoading: boolean,
  quizNav: QuizNav
): [
  Model,
  number,
  ((props: TransitionComponentProps<User>) => JSX.Element) | undefined,
  string | undefined,
  Dispatch<SetStateAction<string | null>>,
  QuizSection,
  User,
  EmailAccountGateState,
  Dispatch<SetStateAction<EmailAccountGateState>>,
  AccountExistsCheck | null,
  Dispatch<SetStateAction<AccountExistsCheck | null>>
] {
  const [pageNo, setPageNo] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [progressPercent, setProgressPercent] = useState(0);
  const [section, setSection] = useState<QuizSection>(
    QuizSection.GETTING_STARTED
  );
  const [user, setUser] = useState<User>({} as User);
  const [transitionComponentKey, setTransitionComponentKey] = useState<
    string | null
  >(null);
  const [emailAccountGate, setEmailAccountGate] = useState<EmailAccountGateState>(
    createIdleEmailAccountGateState
  );
  const [accountExistsCheck, setAccountExistsCheck] =
    useState<AccountExistsCheck | null>(null);
  const emailAccountGateBypassRef = useRef<string | null>(null);
  const emailAccountGateInFlightRef = useRef<string | null>(null);
  // web43_gym_buddy_clarity: arm for the survey handlers + one exposure per visit.
  const web43ArmRef = useRef<string | undefined>(undefined);
  const web43ExposedRef = useRef(false);
  web43ArmRef.current = statsigClient ? getWeb43Arm(statsigClient) : undefined;
  // web32_scale_question: fire the experiment exposure at most once.
  const web32ScaleExposedRef = useRef(false);
  const web38HsaExposedRef = useRef(false);
  const transitionComponent = useMemo(() => {
    if (isTransitionComponentKey(transitionComponentKey)) {
      return COMPONENT_CONFIG[transitionComponentKey].component;
    }
  }, [transitionComponentKey]);
  const transitionComponentName = useMemo(() => {
    if (isTransitionComponentKey(transitionComponentKey)) {
      return COMPONENT_CONFIG[transitionComponentKey].componentName;
    }
  }, [transitionComponentKey]);

  const navigate = useNavigate();
  const { trackAnswer } = useAnswerTracker();

  const survey = useMemo(() => new Model(surveyJson), [surveyJson]);

  survey.onGetQuestionTitleActions.add((_, options) => {
    (options as any).actions = [];
  });

  survey.onUpdateQuestionCssClasses.add(function (survey, options) {
    const leftAlignQuestionNames = ["goals"];
    if (
      leftAlignQuestionNames.includes(options.question.name) &&
      !options.cssClasses.root.includes("left-align")
    ) {
      options.cssClasses.root += " left-align";
    }
  });

  const onAfterRenderPageHandler = useMemo(
    () =>
      onAfterRenderPageFuncGenerator(
        setProgressPercent,
        setPageTotal,
        setSection,
        quizNav
      ),
    [setProgressPercent, setPageTotal, setSection, quizNav]
  );
  const markdownToHtmlConverter = useMemo(
    () => markdownToHtmlFuncGenerator(converter),
    [converter]
  );
  const onCurrentPageChangedHandler = useMemo(
    () =>
      onCurrentPageChangedFuncGenerator(
        COMPONENT_CONFIG,
        setTransitionComponentKey,
        setUser,
        trackAnswer,
        quizNav
      ),
    [setTransitionComponentKey, setUser, trackAnswer, quizNav]
  );
  const onCompletingHandler = useMemo(
    () => onCompletingFuncGenerator(setTransitionComponentKey, setUser, quizNav),
    [setTransitionComponentKey, setUser, quizNav]
  );
  const onCompleteHandler = () => {
    // Fire tracking events for quiz completion
    trackCustom("QuizFinished");
    trackQuizCompleted();

    if (window.location.pathname !== "/checkout") {
      navigate("/results");
    }
  };

  const onCurrentPageChangingHandler = useCallback(
    (sender: any, options: any) => {
      if (!options.isNextPage) {
        return;
      }

      // web43 variation: choosing "Yes, add a second person" goes straight
      // past the guest-pass screen; "No thanks" still lands on it.
      if (
        options.oldCurrentPage?.name === GYM_BUDDY_ENTRY_PAGE &&
        options.newCurrentPage?.name === GUEST_PASS_PAGE &&
        web43ArmRef.current === WEB43_ARM_VARIATION &&
        isGymBuddyOptedIn(sender.getValue(GYM_BUDDY_ENTRY_PAGE))
      ) {
        const afterGuestPass = sender.getPageByName(PAGE_AFTER_GUEST_PASS);
        if (afterGuestPass) {
          // survey-core 1.9 ignores a replaced options.newCurrentPage: cancel
          // this move and jump once the event has unwound.
          options.allowChanging = false;
          options.allow = false;
          setTimeout(() => {
            sender.currentPage = afterGuestPass;
          }, 0);
          return;
        }
      }

      const question = options.oldCurrentPage.name;

      if (question === "email") {
        const email = String(sender.data.email ?? "").trim();
        const emailKey = getAccountExistsEmailKey(email);

        if (!email || options.oldCurrentPage.hasErrors(false)) {
          return;
        }

        if (emailAccountGateBypassRef.current === emailKey) {
          emailAccountGateBypassRef.current = null;
          setEmailAccountGate(createIdleEmailAccountGateState());
        } else {
          const cachedAccountExistsCheck = getAccountExistsCheckForEmail(
            accountExistsCheck,
            email
          );

          if (cachedAccountExistsCheck) {
            if (cachedAccountExistsCheck.exists) {
              setEmailAccountGate({ status: "found", email });
              return;
            }

            setEmailAccountGate(createIdleEmailAccountGateState());
          } else {
            options.allowChanging = false;

            if (emailAccountGateInFlightRef.current === emailKey) {
              return;
            }

            sender.setValue("email", email);
            emailAccountGateInFlightRef.current = emailKey;
            setEmailAccountGate({ status: "checking", email });

            accountExists(email)
              .then((exists) => {
                if (emailAccountGateInFlightRef.current !== emailKey) {
                  return;
                }

                setAccountExistsCheck({ email, emailKey, exists });

                if (exists) {
                  setEmailAccountGate({ status: "found", email });
                  return;
                }

                emailAccountGateBypassRef.current = emailKey;
                setEmailAccountGate(createIdleEmailAccountGateState());
                sender.nextPage();
              })
              .catch((error) => {
                console.error("Failed to check whether account exists", error);
                setAccountExistsCheck({ email, emailKey, exists: false });
                emailAccountGateBypassRef.current = emailKey;
                setEmailAccountGate(createIdleEmailAccountGateState());
                sender.nextPage();
              })
              .finally(() => {
                if (emailAccountGateInFlightRef.current === emailKey) {
                  emailAccountGateInFlightRef.current = null;
                }
              });

            return;
          }
        }
      }

      const answer = sender.data[question];
      trackAnswer(question, answer, sender.data);
    },
    [accountExistsCheck, trackAnswer]
  );

  useEffect(() => {
    const lastUpdated = localStorage.getItem(LAST_UPDATED_KEY);
    const currentTime = Date.now();
    if (lastUpdated) {
      if (currentTime - parseInt(lastUpdated, 10) > SEVEN_DAY_IN_MS) {
        // Clear localStorage and reset form
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        localStorage.removeItem(LAST_UPDATED_KEY);
        survey.currentPageNo = 0;
      }
    }

    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { currentPageIndex = 0, currentPageName = null } = storedSurvey
      ? JSON.parse(storedSurvey)
      : {};

    if (currentPageIndex && storedSurvey) {
      const { data } = JSON.parse(storedSurvey);
      Object.entries(data).forEach(([key, value]) => {
        survey.setValue(key, value);
      });
      // Navigate to the saved page. Prefer resolving by NAME against the CURRENT
      // model (campaign reordering can shift indices), and fall back to the
      // clamped numeric index for legacy blobs or names that no longer resolve
      // (e.g. a merged-and-dropped page).
      const savedPage =
        typeof currentPageName === "string" && currentPageName
          ? survey.getPageByName(currentPageName)
          : null;
      if (savedPage) {
        survey.currentPageNo = survey.visiblePages.indexOf(savedPage);
      } else {
        const maxPageNo = Math.max(0, survey.visiblePageCount - 1);
        survey.currentPageNo = Math.min(Math.max(0, currentPageIndex), maxPageNo);
      }
      // setUser((prevUser) => ({ ...prevUser, ...data }));
      // Ensure values invalid for the current experience are cleared
      const experienceValue = survey.data?.experience;
      if (experienceValue === "Beginner") {
        const invalidNumberWorkouts = ["As many as possible"];
        if (invalidNumberWorkouts.includes(survey.data?.numberWorkouts)) {
          survey.setValue("numberWorkouts", undefined);
        }
        if (survey.data?.timeAvailable === "30OrLess") {
          survey.setValue("timeAvailable", undefined);
        }
      }
    }

    const saveProgress = (a: any, b: any) => {
      const currentData = {
        currentPageIndex: survey.currentPageNo,
        currentPageName: survey.currentPage?.name,
        data: survey.data,
      };
      // Some keys in survey data can become undefined during the survey flow,
      // so we spread the user object here to ensure we don't lose any data
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
          ...currentData,
          data: { ...user, ...currentData.data },
        })
      );
      localStorage.setItem(LAST_UPDATED_KEY, Date.now().toString());
    };

    const customGuestPassSkipNavigation = (sender: any, options: any) => {
      const page = sender.currentPage;
      if (!page) return;

      const existing = document.querySelector("#skipButton");
      if (existing) existing.remove();

      if(page.name !== "guestPassInvites" && page.name !== "togetherConsistent") {
        return;
      }
      // web43 variation: the entry page renders "No thanks, just me" itself.
      if (page.props?.secondaryButtonText) {
        return;
      }

      // find nav bar (QuestionFooter's container; its text is translated, so
      // it is found by id rather than by the button's English label)
      const parentNode = document.getElementById("question-footer-container");
      if (!parentNode) return;

      // only show Skip on page that has the guestPassInvites question
      const guestPassQ = sender.getQuestionByName("guestPassInvites");
      const togetherConsistentQ = sender.getQuestionByName("togetherConsistent");
      const isOnTogetherConsistentPage = page.containsElement(togetherConsistentQ);
      const isOnGuestPassInvitesPage = page.containsElement(guestPassQ);
      if ((!guestPassQ || !isOnGuestPassInvitesPage) && (!togetherConsistentQ || !isOnTogetherConsistentPage)) return;

      // create Skip button
      const btn = document.createElement("button");
      btn.id = "skipButton";
      btn.type = "button";
      btn.textContent = i18n.t("quiz.guestPassInvites.skip");

      // style so it shows under Continue
      btn.style.marginTop = "8px";
      btn.style.textAlign = "center";
      btn.style.backgroundColor = "white";
      btn.style.border = "0";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "16px"
      btn.style.color = "#4A4A4A";
      btn.style.fontStyle = "normal";
      btn.style.lineHeight = "1.5";
      btn.style.fontWeight = "400";
      btn.style.marginTop = "16px";

      // click handler: go to page with followingus
      btn.addEventListener("click", () => {
        console.log("btn clicked");
        console.log("togetherConsistentQ", togetherConsistentQ);
        if (isOnTogetherConsistentPage) {
          console.log("setting togetherConsistent to false");
          sender.setValue("togetherConsistent", false);
          amplitude.track("GymBuddyPlanSkipped");
          sender.currentPage = sender.getPageByQuestion(guestPassQ);
        } else {
          const followingQ = sender.getQuestionByName("followingus");
          console.log("followingQ", followingQ);
          if (followingQ) {
            sender.currentPage = sender.getPageByQuestion(followingQ);
          }
        }
      });

      // append to nav bar
      parentNode.appendChild(btn);
    }

    const project60OptOut = (sender: any, options: any) => {
      const page = sender.currentPage;

      const existing = document.querySelector("#optOutButton");
      if (existing) existing.remove();

      if(page.name !== "project60") {
        return;
      }
      const btn = document.createElement("button");
      btn.id = "optOutButton";
      btn.type = "button";
      btn.textContent = "Opt out";
      btn.className = "project60-opt-out-button";

      // style so it shows under Continue
      btn.style.marginTop = "8px";
      btn.style.textAlign = "center";
      btn.style.backgroundColor = "white";
      btn.style.border = "0";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "16px"
      btn.style.color = "#4A4A4A";
      btn.style.fontStyle = "normal";
      btn.style.lineHeight = "1.5";
      btn.style.fontWeight = "400";
      btn.style.marginTop = "16px";

      // click handler: go to page with followingus
      btn.addEventListener("click", () => {
        console.log("sender", sender);
        sender.currentPage.questions[0].value = false;
        sender.nextPage();
      });

      const container = document.getElementById("question-footer-container");
      if (container?.querySelectorAll("#optOutButton")?.length === 0) {
        document.getElementById("question-footer-container")?.appendChild(btn);
      }
    }

    // Custom auto-advance logic based on page configuration
    const customAutoAdvanceHandler =  (sender: any) => {
      const currentPage = sender.currentPage;
      if (!currentPage) return;

      // Transformed pages can be absent from static SURVEY_JSON, so read the
      // active model config to preserve their noAutoAdvance setting.
      const pageConfig = (((surveyJson as any)?.pages) ?? SURVEY_JSON.pages).find(
        (p: any) => p.name === currentPage.name
      ) as any;

      // Skip auto-advance if page has noAutoAdvance property set to true
      if (pageConfig?.noAutoAdvance) {
        return;
      }

      // Check if all questions on the current page are answered
      const questions = currentPage.questions;
      for (let i = 0; i < questions.length; i++) {
        if (questions[i].hasInput && questions[i].isEmpty()) return;
      }

      // Check for errors
      if (currentPage.hasErrors(false)) return;

      // Auto-advance to next page with a small delay to prevent ghost taps
      if (!sender.isLastPage) {
        // Clear any active element focus/highlight
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && typeof activeElement.blur === "function") {
          activeElement.blur();
        }

        // Add transitioning class to prevent interactions during page change
        const surveyRoot = document.querySelector(".sd-root-modern");
        if (surveyRoot) {
          surveyRoot.classList.add("transitioning");
        }

        // Add a small delay before navigating to prevent ghost taps on mobile
        setTimeout(() => {
          sender.nextPage();
          // Remove transitioning class after navigation
          setTimeout(() => {
            if (surveyRoot) {
              surveyRoot.classList.remove("transitioning");
            }
          }, 50);
        }, 50);
      } else {
        sender.completeLastPage();
      }
    };

    // Handler to add inputmode="numeric" for number inputs
    const afterRenderQuestionInputHandler = (sender: any, options: any) => {
      const { htmlElement } = options;

      // Check if the htmlElement is an input with type="number"
      if (htmlElement && htmlElement.type === "number") {
        // Set inputmode to numeric for better mobile keyboard
        htmlElement.setAttribute("inputmode", "numeric");
      }
    };

    // Disable default auto-advance and use custom logic
    console.log("adding customAutoAdvanceHandler");
    if (!didAddCustomAutoAdvanceHandler) {
      didAddCustomAutoAdvanceHandler = true;
      survey.onValueChanged.add(customAutoAdvanceHandler);
    }

    // Clear dependent answers when experience changes
    const experienceChangeHandler = (sender: any, options: any) => {
      if (!options || options.name !== "experience") return;
      const experienceValue = sender.data?.experience;
      if (experienceValue === "Beginner") {
        const invalidNumberWorkouts = ["As many as possible"];
        if (invalidNumberWorkouts.includes(sender.data?.numberWorkouts)) {
          sender.setValue("numberWorkouts", undefined);
        }
        if (sender.data?.timeAvailable === "30OrLess") {
          sender.setValue("timeAvailable", undefined);
        }
      }
    };

    const experimentEnrollmentHandler = (sender: any, options: any) => {
      console.log("experimentEnrollmentHandler", options.newCurrentPage.name);
      // Handler for the youngAgeSplash page
      // This page should only be shown if users is enrolled in the web17_2 experiment
      if (options.newCurrentPage.name !== "togetherConsistent") {
        console.log("skipping togetherConsistent, not the current page");
        return;
      }
    };

    // web38_hsa_fsa_demand: the two measurement events from the doc. "viewed"
    // fires on render (so a reload or restore onto the page still counts) and
    // once per visit to the page.
    let web38HsaViewedPage = false;
    const web38HsaViewedHandler = (_sender: any, options: any) => {
      if (options?.page?.name !== WEB38_HSA_QUESTION_NAME) {
        web38HsaViewedPage = false;
        return;
      }
      if (web38HsaViewedPage) return;
      web38HsaViewedPage = true;
      amplitude.track("hsa_question_viewed");
    };
    const web38HsaAnsweredHandler = (_sender: any, options: any) => {
      if (!options || options.name !== WEB38_HSA_QUESTION_NAME || !options.value) return;
      amplitude.track("hsa_question_answered", { hsa_answer: options.value });
    };

    // web43_gym_buddy_clarity: exposure for both arms when the entry page
    // renders (the first screen that differs), once per visit.
    const web43EntryViewedHandler = (_sender: any, options: any) => {
      if (options?.page?.name !== GYM_BUDDY_ENTRY_PAGE || web43ExposedRef.current) return;
      const arm = web43ArmRef.current;
      if (!arm) return;
      web43ExposedRef.current = true;
      amplitude.track("ExperimentStarted", {
        experimentName: WEB43_EXPERIMENT_NAME,
        variantName: arm,
      });
    };

    survey.onCurrentPageChanged.add(experimentEnrollmentHandler);
    survey.onAfterRenderPage.add(web38HsaViewedHandler);
    survey.onAfterRenderPage.add(web43EntryViewedHandler);
    survey.onValueChanged.add(web38HsaAnsweredHandler);
    survey.onValueChanged.add(experienceChangeHandler);
    survey.onValueChanged.add(saveProgress);
    survey.onCurrentPageChanged.add(saveProgress);
    survey.sendResultOnPageNext = true;
    survey.onTextMarkdown.add(markdownToHtmlConverter);
    survey.onAfterRenderPage.add(onAfterRenderPageHandler);
    survey.onAfterRenderQuestionInput.add(afterRenderQuestionInputHandler);
    survey.onCurrentPageChanged.add(onCurrentPageChangedHandler);
    survey.onCompleting.add(onCompletingHandler);
    survey.onComplete.add(onCompleteHandler);
    survey.onCurrentPageChanging.add(onCurrentPageChangingHandler);
    survey.onAfterRenderPage.add(customGuestPassSkipNavigation);
    survey.onAfterRenderPage.add(project60OptOut);

    return () => {
      survey.onCurrentPageChanged.remove(experimentEnrollmentHandler);
      survey.onAfterRenderPage.remove(web38HsaViewedHandler);
      survey.onValueChanged.remove(web38HsaAnsweredHandler);
      survey.onValueChanged.remove(customAutoAdvanceHandler);
      survey.onAfterRenderPage.remove(customGuestPassSkipNavigation);
      survey.onAfterRenderPage.remove(project60OptOut);
      survey.onValueChanged.remove(experienceChangeHandler);
      survey.onValueChanged.remove(saveProgress);
      survey.onCurrentPageChanged.remove(saveProgress);
      survey.onTextMarkdown.remove(markdownToHtmlConverter);
      survey.onAfterRenderPage.remove(onAfterRenderPageHandler);
      survey.onAfterRenderQuestionInput.remove(afterRenderQuestionInputHandler);
      survey.onCurrentPageChanged.remove(onCurrentPageChangedHandler);
      survey.onCompleting.remove(onCompletingHandler);
      survey.onComplete.remove(onCompleteHandler);
      survey.onCurrentPageChanging.remove(onCurrentPageChangingHandler);
      didAddCustomAutoAdvanceHandler = false;
    };
  }, [
    markdownToHtmlConverter,
    onAfterRenderPageHandler,
    onCurrentPageChangedHandler,
    onCurrentPageChangingHandler,
    onCompletingHandler,
    onCompleteHandler,
    survey,
  ]);

  useEffect(() => {
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { currentPageIndex = 0 } = storedSurvey
      ? JSON.parse(storedSurvey)
      : {};

    if (currentPageIndex && storedSurvey) {
      const { data } = JSON.parse(storedSurvey);
      setUser((prevUser) => ({ ...prevUser, ...data }));
    }
  }, []);

  // web32_scale_question: log exposure for both arms as soon as Statsig is
  // ready; single adhoc question, no qualifying trigger, all traffic eligible.
  useEffect(() => {
    if (
      !statsigClient ||
      isStatsigClientLoading ||
      web32ScaleExposedRef.current
    ) {
      return;
    }
    const arm = getAssignedExperimentArm(
      WEB32_SCALE_EXPERIMENT_NAME,
      statsigClient
    );
    web32ScaleExposedRef.current = true;
    amplitude.track("ExperimentStarted", {
      experimentName: WEB32_SCALE_EXPERIMENT_NAME,
      variantName: arm ?? "control",
    });
  }, [statsigClient, isStatsigClientLoading]);

  // web38_hsa_fsa_demand: log exposure for both arms as soon as Statsig is
  // ready. Enrollment is US-only via the experiment's targeting gate, so a
  // visitor outside the gate gets NO arm back — log nothing for them rather
  // than counting them as control.
  useEffect(() => {
    if (
      !statsigClient ||
      isStatsigClientLoading ||
      web38HsaExposedRef.current
    ) {
      return;
    }
    const arm = getAssignedExperimentArm(
      WEB38_HSA_EXPERIMENT_NAME,
      statsigClient
    );
    web38HsaExposedRef.current = true;
    if (!arm) return;
    amplitude.track("ExperimentStarted", {
      experimentName: WEB38_HSA_EXPERIMENT_NAME,
      variantName: arm,
    });
  }, [statsigClient, isStatsigClientLoading]);

  useEffect(() => {
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
    const { data = {} } = storedSurvey ? JSON.parse(storedSurvey) : {};
    const isSurveyComplete = !!data.focus;

    if (
      (window.location.pathname === "/results" ||
        window.location.pathname === "/checkout" ||
        window.location.pathname.startsWith("/reserve")) &&
      isSurveyComplete
    ) {
      return;
    }
    if (window.location.pathname === "/bridge") {
      return;
    }
    navigate(`/page/${survey.currentPageNo + 1}`);
  }, [survey.currentPageNo, survey.currentPage.name, navigate]);

  return [
    survey,
    progressPercent,
    transitionComponent,
    transitionComponentName,
    setTransitionComponentKey,
    section,
    user,
    emailAccountGate,
    setEmailAccountGate,
    accountExistsCheck,
    setAccountExistsCheck,
  ];
}

function App() {
  var converter = new showdown.Converter();
  useImagePreloader();
  // const questions = SURVEY_JSON.pages.map(page => ({ name: page.name, title: page.elements[0].title }));
  // // take that survey json and convert it to a csv

  // return <>
  //   <div>
  //     {questions.map(question => <div>{question.name + ', ' + question.title}</div>)}
  //   </div>
  // </>;

  const [assignedExperiment, setAssignedExperiment] = useState<
    { experiment: string; arm: string } | undefined
  >(undefined);
  const overrideStableID = useMemo(
    () => Cookies.get("bws_statsig_stable_id"),
    []
  );

  // Bridge CMS: stable id for rollout bucketing + load published bridges once.
  const bridgeBucketId = useMemo(
    () => getStableBucketId(overrideStableID),
    [overrideStableID]
  );
  const [cmsBridgesReady, setCmsBridgesReady] = useState(false);
  useEffect(() => {
    let active = true;
    // Always resolves (falls back to bundled content on failure) so routing never hangs.
    fetchBridges().finally(() => {
      if (active) setCmsBridgesReady(true);
    });
    return () => {
      active = false;
    };
  }, []);
  const { client, isLoading } = useClientAsyncInit(
    STATSIG_CLIENT_KEY,
    {
      customIDs: {
        stableID: overrideStableID,
      },
    },
    {
      environment: {
        tier: process.env.NODE_ENV,
      },
    }
  );

  // ----- Intro Sequence (per-campaign) --------------------------------------
  // utm_angle: prefer the URL param on this load, fall back to the persisted
  // value, matching how the rest of the file reads it.
  const utmAngle = useMemo(() => {
    const fromUrl = new URL(window.location.href).searchParams.get("utm_angle");
    return fromUrl || localStorage.getItem("utm_angle");
  }, []);

  // web39_paid_opening_arc (the opening arc, built as web33): paid-ad visitors
  // on the already_know_why_main angle are enrolled once the CMS has resolved
  // (bridge-control visitors are redirected away first and never enrolled); a
  // ?debug / ?force pin also counts. Variation = the arc INSTEAD of the bridge
  // screen + intro sequence. Everyone else: OPENING_ARC_ORGANIC_DEFAULT for
  // organic, the plain opening for other paid traffic.
  const web39Forced = !!getForcedExperimentArm(WEB39_EXPERIMENT_NAME);
  const web39Eligible =
    web39Forced ||
    (cmsBridgesReady &&
      isWeb39Eligible(utmAngle) &&
      getBridgeRoute(utmAngle, bridgeBucketId) !== "control");
  const web39Arm =
    web39Eligible && client
      ? getAssignedExperimentArm(WEB39_EXPERIMENT_NAME, client)
      : undefined;
  const web39Variation = web39Arm === WEB39_ARM_VARIATION;
  const openingArcOn = web39Eligible
    ? web39Variation
    : OPENING_ARC_ORGANIC_DEFAULT && !isPaidAdTraffic();
  // Exposure for both arms, once, as soon as the arm is known (no qualifying
  // trigger: the arc starts on the first screen). No arm (experiment not
  // started, or Statsig unavailable) = no event, so nothing is logged before
  // the test is live.
  const web39ExposedRef = useRef(false);
  useEffect(() => {
    if (!web39Eligible || !client || isLoading || !web39Arm || web39ExposedRef.current) return;
    web39ExposedRef.current = true;
    amplitude.track("ExperimentStarted", {
      experimentName: WEB39_EXPERIMENT_NAME,
      variantName: web39Arm,
    });
  }, [web39Eligible, client, isLoading, web39Arm]);

  // Empty until the CMS resolves -> default flow (no reordering) during the
  // initial render, then the campaign order once bridges are ready. CONTROL users
  // never get the reorder: they're the baseline, and a control visitor sent to the
  // control page and back still carries utm_angle (via localStorage), so without
  // this guard the quiz would reorder/corrupt their flow. The web39 variation
  // skips the campaign flow entirely (arc instead of bridge + intro sequence).
  const introSequence =
    cmsBridgesReady &&
    !web39Variation &&
    getBridgeRoute(utmAngle, bridgeBucketId) !== "control"
      ? getIntroSequence(utmAngle)
      : [];
  const showBridge =
    cmsBridgesReady &&
    !web39Variation &&
    !!utmAngle &&
    shouldShowBridge(utmAngle, bridgeBucketId);
  // The first intro question is "merged" into the bridge (and its page omitted
  // from the Model) only when the bridge is shown AND has a matching question
  // block for that exact name.
  const introBlocks = showBridge ? getBridgeBlocks(utmAngle) : [];
  const hasMatchingQ = introBlocks.some(
    (b) => b.type === "question" && b.questionName === introSequence[0]
  );
  const mergedName =
    showBridge && hasMatchingQ && introSequence.length > 0
      ? introSequence[0]
      : null;

  const baseCampaignSurveyJson = useMemo(
    () =>
      cmsBridgesReady
        ? buildCampaignSurveyJson(introSequence, mergedName)
        : SURVEY_JSON,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cmsBridgesReady, introSequence.join(","), mergedName]
  );
  const web32ScaleArm = client
    ? getAssignedExperimentArm(WEB32_SCALE_EXPERIMENT_NAME, client)
    : undefined;
  const web32ScaleOn = web32ScaleArm === WEB32_SCALE_ARM_VARIATION;
  const web38HsaArm = client
    ? getAssignedExperimentArm(WEB38_HSA_EXPERIMENT_NAME, client)
    : undefined;
  const web38HsaOn = web38HsaArm === WEB38_HSA_ARM_VARIATION;
  const web43Arm = client ? getWeb43Arm(client) : undefined;
  const web43On = web43Arm === WEB43_ARM_VARIATION;
  const campaignSurveyJson = useMemo(() => {
    const basePages = (baseCampaignSurveyJson as any).pages;
    const p33 = buildOpeningArcPages(basePages, openingArcOn);
    const pScale = buildScaleQuestionPages(p33 as any, web32ScaleOn);
    const pHsa = buildHsaQuestionPages(pScale as any, web38HsaOn);
    const pGym = buildGymBuddyClarityPages(pHsa as any, web43On);
    if (pGym === basePages) return baseCampaignSurveyJson;
    return { ...baseCampaignSurveyJson, pages: pGym };
  }, [baseCampaignSurveyJson, openingArcOn, web32ScaleOn, web38HsaOn, web43On]);
  const quizNav = useMemo(
    () => makeQuizNav((campaignSurveyJson as any).pages),
    [campaignSurveyJson]
  );

  const [
    survey,
    progressPercent,
    TransitionComponent,
    transitionComponentName,
    setTransitionComponentKey,
    progressTitle,
    user,
    emailAccountGate,
    setEmailAccountGate,
    accountExistsCheck,
    setAccountExistsCheck,
  ] = useSurvey(campaignSurveyJson, converter, client, isLoading, quizNav);

  // Spanish quiz (web47_spanish_quiz): resolve the language once Statsig is
  // ready, before the first screen renders; see src/i18n/language.ts.
  const { t } = useTranslation();
  const [quizLanguage, setQuizLanguage] = useState<QuizLanguage | null>(null);
  useEffect(() => {
    if (isLoading || quizLanguage) return;
    const lang = resolveQuizLanguage(client);
    applyQuizLanguage(lang, survey);
    setQuizLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  useEffect(() => {
    if (quizLanguage) applySurveyLanguage(survey, quizLanguage);
  }, [survey, quizLanguage]);
  const languageContext = useMemo(
    () =>
      quizLanguage
        ? {
            language: quizLanguage,
            setLanguage: (lang: QuizLanguage) => {
              saveLanguageChoice(lang);
              applyQuizLanguage(lang, survey);
              setQuizLanguage(lang);
            },
          }
        : null,
    [quizLanguage, survey]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { referral, pending: referralPending } = useReferral();
  // Referral visitors keep the standard trial offer: no web46 enrollment.
  const web46Arm = referral || referralPending ? undefined : getWeb46Arm(client);
  const isQuizRoute = location.pathname.startsWith('/page/') || location.pathname === '/bridge';
  useEffect(() => {
    if (!isLoading && isQuizRoute && web46Arm) trackWeb46Exposure(web46Arm);
  }, [isLoading, isQuizRoute, web46Arm]);
  // web48_timer_banner: the banner runs from the first quiz screen (the home
  // page for the quiz) through the pricing page. Referral visitors keep their
  // referral banner: no web48 enrollment.
  const web48Arm = isLoading || referral || referralPending ? undefined : getWeb48Arm(client);
  const web48EndsAt = isLoading ? undefined : getWeb48EndsAt(client);
  useEffect(() => {
    // Persist arm + end date for the offer state (web29Offer) and the checkout
    // page load; clear it once Statsig says this visitor has no arm.
    if (isLoading || referralPending) return;
    writeWeb48Offer(web48Arm ? { arm: web48Arm, endsAt: web48EndsAt } : null);
  }, [isLoading, referralPending, web48Arm, web48EndsAt]);
  useEffect(() => {
    if (isQuizRoute && web48Arm) trackWeb48Exposure(web48Arm);
  }, [isQuizRoute, web48Arm]);
  // Black Friday 2026 sale (bfSale.ts): banner on every quiz page, the sale
  // price on pricing and checkout. Referral visitors keep their referral
  // offer (Core's discount route cannot carry a referral code).
  const bfSale = isLoading || referral || referralPending ? null : getBfSale(client);
  const bfBannerDesign = bfSale ? getBfBannerDesign(bfSale, web48Arm) : undefined;
  const bfSaleKey = bfSale ? `${bfSale.phase}:${bfSale.percent}:${bfSale.endsAt}` : "";
  useEffect(() => {
    if (isLoading || referralPending) return;
    writeStoredBfSale(bfSale);
    setSalePhase(toSalePhaseTag(bfSale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, referralPending, bfSaleKey]);
  const bfSaleRef = useRef(bfSale);
  bfSaleRef.current = bfSale;
  useEffect(() => {
    // Gym Buddy screen (togetherConsistent): during the sale the marked
    // discount line states the sale on top of the two-person discount.
    const gymBuddySaleCopy = (_: Model, options: { html: string; reason: string }) => {
      const sale = bfSaleRef.current;
      if (!sale || !options.html.includes("data-gb-discount")) return;
      const text = i18n.t("splash.gymBuddy.togetherConsistent.checkItems.discountSale", { percent: sale.percent });
      options.html = options.html.replace(/(<span data-gb-discount="">)[^<]*(<\/span>)/, `$1${text}$2`);
    };
    survey.onProcessHtml.add(gymBuddySaleCopy);
    return () => {
      survey.onProcessHtml.remove(gymBuddySaleCopy);
    };
  }, [survey]);
  const isCheckoutRoute = useMatch("/checkout") !== null;
  const initialLoadRef = useRef(true);
  const initialHrefRef = useRef(window.location.href);
  const [isInitialFunnelRouting, setIsInitialFunnelRouting] = useState(true);
  const signupAccountGateInFlightRef = useRef<string | null>(null);
  const isSignupAccountGateRoute = isSignupAccountGatePath(location.pathname);
  const signupAccountGateEmail = isSignupAccountGateRoute
    ? getTrimmedEmail(user.email) ??
      getTrimmedEmail(survey.data?.email) ??
      getStoredSurveyEmail()
    : null;
  const checkedSignupAccount = signupAccountGateEmail
    ? getAccountExistsCheckForEmail(accountExistsCheck, signupAccountGateEmail)
    : null;
  const shouldBlockSignupAccountGate =
    isSignupAccountGateRoute &&
    !!signupAccountGateEmail &&
    (checkedSignupAccount == null || checkedSignupAccount.exists);

  const clearAccountExistsGate = useCallback(() => {
    setTransitionComponentKey(null);
    setEmailAccountGate(createIdleEmailAccountGateState());
    setAccountExistsCheck(null);
  }, [
    setAccountExistsCheck,
    setEmailAccountGate,
    setTransitionComponentKey,
  ]);

  const handleEmailAccountExistsBack = useCallback(() => {
    clearAccountExistsGate();
  }, [clearAccountExistsGate]);

  const handleSignupAccountExistsBack = useCallback(() => {
    const emailPageIndex = quizNav.findPageIndex("email");
    const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);

    initialLoadRef.current = false;
    survey.currentPageNo = emailPageIndex;
    clearAccountExistsGate();

    if (storedSurvey) {
      try {
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({
            ...JSON.parse(storedSurvey),
            currentPageIndex: emailPageIndex,
            currentPageName: "email",
          })
        );
      } catch {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }

    navigate(`/page/${emailPageIndex + 1}`, { replace: true });
  }, [
    clearAccountExistsGate,
    navigate,
    survey,
    quizNav,
  ]);

  function isValidUUIDv4(uuid: unknown): boolean {
    if (typeof uuid !== "string") {
      return false;
    }

    // UUID v4 format:
    // xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    // where x is any hexadecimal digit and y is one of 8, 9, A, or B
    const uuidV4Regex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    return uuidV4Regex.test(uuid);
  }

  useEffect(() => {
    // Only capture tracking parameters if they are present in the URL
    const captureUtmParams = () => {
      const urlParams = new URLSearchParams(location.search);
      const amplitudeDeviceIdParam =
        urlParams.get("amplitude_device_id") ?? urlParams.get("amplitudeId");

      if (isValidUUIDv4(amplitudeDeviceIdParam)) {
        if (amplitudeDeviceIdParam) {
          Cookies.set("bws_amplitude_device_id", amplitudeDeviceIdParam, {
            expires: 30,
            sameSite: "lax",
            domain:
              process.env.NODE_ENV === "development"
                ? "localhost"
                : "builtwithscience.com",
          });
        }
      }

      const params = getCapturedTrackingParams(location.search);

      if (params) {
        persistTrackingParams(
          params,
          process.env.NODE_ENV === "development"
            ? "localhost"
            : "builtwithscience.com"
        );
      }

      // Capture utm_angle for bridge screen experiment
      const utmAngle = urlParams.get("utm_angle");
      if (utmAngle) {
        localStorage.setItem("utm_angle", utmAngle);
      }
    };

    captureUtmParams();
  }, [location.search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const iacConnectionId = urlParams.get("iacConnectionId");
    if (iacConnectionId) {
      localStorage.setItem("iacConnectionId", iacConnectionId);
    }
    const mode = urlParams.get("mode");
    if (mode) {
      localStorage.setItem("mode", mode);
    }
    // Capture hs_offer query param for Hubspot promotional offer tracking
    const hsOffer = urlParams.get("hs_offer");
    if (hsOffer) {
      localStorage.setItem("hs_offer", hsOffer);
    }
    applyCheckoutTestModeOverride(location.search);
  }, [location.search]);

  useEffect(() => {
    if (keepForcedCheckoutTestModeEnabled()) {
      return;
    }

    axios
      .get(
        "https://core.builtwithscience.com/internal/public/control-flag/get/checkoutv2"
      )
      .then((response) => {
        localStorage.setItem(
          CHECKOUT_V2_STORAGE_KEY,
          response.data.data ? "enabled" : "disabled"
        );
      })
      .catch(() => {
        localStorage.setItem(CHECKOUT_V2_STORAGE_KEY, "disabled");
      });
  }, []);

  const onComplete = useCallback(() => {
    if (transitionComponentName != null) {
      sendPageViewEvent(
        user.goals,
        transitionComponentName,
        "",
        [],
        quizNav.findPageIndex(transitionComponentName) + 1
      );
    }
    setTransitionComponentKey(null);
  }, [setTransitionComponentKey, transitionComponentName, user.goals, quizNav]);

  useEffect(() => {
    if (!isSignupAccountGateRoute || !signupAccountGateEmail) {
      return;
    }

    if (checkedSignupAccount) {
      if (checkedSignupAccount.exists) {
        setEmailAccountGate({
          status: "found",
          email: checkedSignupAccount.email,
        });
      }
      return;
    }

    const email = signupAccountGateEmail;
    const emailKey = getAccountExistsEmailKey(email);

    if (signupAccountGateInFlightRef.current === emailKey) {
      return;
    }

    signupAccountGateInFlightRef.current = emailKey;
    setEmailAccountGate({ status: "checking", email });

    accountExists(email)
      .then((exists) => {
        if (signupAccountGateInFlightRef.current !== emailKey) {
          return;
        }

        setAccountExistsCheck({ email, emailKey, exists });

        if (exists) {
          setEmailAccountGate({ status: "found", email });
          return;
        }

        setEmailAccountGate(createIdleEmailAccountGateState());
      })
      .catch((error) => {
        if (signupAccountGateInFlightRef.current !== emailKey) {
          return;
        }

        console.error("Failed to check whether account exists", error);

        setAccountExistsCheck({ email, emailKey, exists: false });
        setEmailAccountGate(createIdleEmailAccountGateState());
      })
      .finally(() => {
        if (signupAccountGateInFlightRef.current === emailKey) {
          signupAccountGateInFlightRef.current = null;
        }
      });
  }, [
    checkedSignupAccount,
    isSignupAccountGateRoute,
    navigate,
    setAccountExistsCheck,
    setEmailAccountGate,
    setTransitionComponentKey,
    signupAccountGateEmail,
    survey,
  ]);

  useEffect(() => {
    if (
      window.location.pathname === "/results" ||
      window.location.pathname === "/checkout" ||
      window.location.pathname.startsWith("/reserve")
    ) {
      initialLoadRef.current = false;
      setIsInitialFunnelRouting(false);
      return;
    }

    // Control variant: redirect this slice of the angle's traffic away from the
    // funnel (to the bridge's configurable control URL, default builtwithscience.com)
    // before any bridge/quiz UI renders. Only on the first load, once the CMS is in.
    if (initialLoadRef.current && cmsBridgesReady) {
      const initialUrl = new URL(initialHrefRef.current);
      const ctrlAngle = initialUrl.searchParams.get("utm_angle");
      if (!isReferralLink(initialUrl.search) && ctrlAngle && getBridgeRoute(ctrlAngle, bridgeBucketId) === "control") {
        initialLoadRef.current = false;
        // Use the ORIGINAL href's query (the app may have rewritten the URL by now)
        // so the carried UTMs survive. buildBridgeControlRedirectUrl strips utm_angle
        // (no loop) and tags bws_quiz_redirect=<angle>_bridge_control (attribution).
        window.location.replace(
          buildBridgeControlRedirectUrl(initialUrl.search, getControlUrl(ctrlAngle))
        );
        return;
      }
    }

    if (window.location.pathname === "/bridge") {
      if (!initialLoadRef.current) {
        setIsInitialFunnelRouting(false);
        return;
      }

      // Wait for the CMS bridges to load before deciding (and for Statsig when
      // this visitor is in the web39 paid test, whose variation skips the bridge).
      if (!cmsBridgesReady) return;
      if (web39Eligible && isLoading) return;

      initialLoadRef.current = false;
      const utmAngle = new URL(initialHrefRef.current).searchParams.get("utm_angle");
      if (utmAngle && !web39Variation && shouldShowBridge(utmAngle, bridgeBucketId)) {
        setIsInitialFunnelRouting(false);
      } else {
        // No live bridge for this angle, or visitor is outside the rollout: go to the quiz.
        setIsInitialFunnelRouting(false);
        navigate(`/page/1`);
      }
      return;
    }

    if (initialLoadRef.current) {
      const storedSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);

      const { currentPageIndex = 0 } = storedSurvey
        ? JSON.parse(storedSurvey)
        : {};

      const utmAngle = new URL(initialHrefRef.current).searchParams.get("utm_angle");

      // Returning users: navigate to the saved page. When utm_angle is present we
      // wait for the CMS first (mirroring the bridge branch) so the Model is in
      // campaign order before the jump and the saved page resolves correctly.
      if (currentPageIndex) {
        if (utmAngle && !cmsBridgesReady) return;
        initialLoadRef.current = false;
        setIsInitialFunnelRouting(false);
        navigate(`/page/${currentPageIndex + 1}`);
        return;
      }

      // Shared referrals enter at Q1 while preserving campaign attribution.
      // Otherwise retain the usual campaign bridge routing.
      if (!utmAngle || isReferralLink(new URL(initialHrefRef.current).search)) {
        initialLoadRef.current = false;
        setIsInitialFunnelRouting(false);
        navigate(`/page/1`);
        return;
      }

      // utm_angle present: wait for the CMS (and Statsig for the web39 paid
      // test), then let it decide on/off + rollout.
      if (!cmsBridgesReady) return;
      if (web39Eligible && isLoading) return;

      initialLoadRef.current = false;
      if (!web39Variation && shouldShowBridge(utmAngle, bridgeBucketId)) {
        setIsInitialFunnelRouting(false);
        navigate(`/bridge?utm_angle=${encodeURIComponent(utmAngle)}`);
      } else {
        setIsInitialFunnelRouting(false);
        navigate(`/page/1`);
      }
    }
  }, [navigate, cmsBridgesReady, bridgeBucketId, isLoading, web39Eligible, web39Variation]);

  interface UTMParams {
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_term: string | null;
    utm_content: string;
  }

  /* Get From Localstorage


  // Safe extraction with error handling
  const getUTMParams = (): UTMParams | null => {
    try {
      const storedData = localStorage.getItem('utmParams') ?? '';
      return JSON.parse(storedData) as UTMParams;
    } catch (error) {

      return null;
    }
  };

  */

  const hasFiredEvent = useRef(false); // <-- State guard

  useEffect(() => {
    if (!isLoading && !hasFiredEvent.current) {
      const overridebws_amplitude_device_ID = Cookies.get(
        "bws_amplitude_device_id"
      );
      // Fire your one-time event here

      const utm_Params = getUTMParams();
      if (overrideStableID === undefined) {
        Cookies.set("bws_statsig_stable_id", client.getContext().stableID, {
          expires: 365,
          sameSite: "lax",
          domain:
            process.env.NODE_ENV === "development"
              ? "localhost"
              : "builtwithscience.com",
        });
      }

      const quizEventData: QuizEventData = {
        event: "quiz",
        funnel_variation: FunnelVariation.General,
        quizz_name: "New Onboarding Quiz",
        quizz_step: "0",
        quizz_step_name: "Start",
        quizz_version: getVersionNumber(),
        utm_angle: localStorage.getItem("utm_angle") ?? undefined,
      };
      pushToDataLayer(quizEventData);

      let amplitudeDeviceId = uuidv4();
      if (overridebws_amplitude_device_ID === undefined) {
        Cookies.set("bws_amplitude_device_id", amplitudeDeviceId, {
          expires: 365,
          sameSite: "lax",
          domain:
            process.env.NODE_ENV === "development"
              ? "localhost"
              : "builtwithscience.com",
        });
      } else {
        amplitudeDeviceId = overridebws_amplitude_device_ID;
      }

      amplitude.init(AMPLITUDE_API_KEY, undefined, {
        deviceId: amplitudeDeviceId,
        serverUrl: "https://analytics.builtwithscience.com/measure",
        autocapture: false,
      });

      if (utm_Params !== null) {
        const identifyObj = new Identify();
        if (
          utm_Params.utm_source !== null &&
          utm_Params.utm_source !== undefined
        )
          identifyObj.set("utm_source", utm_Params.utm_source);
        if (
          utm_Params.utm_medium !== null &&
          utm_Params.utm_medium !== undefined
        )
          identifyObj.set("utm_medium", utm_Params.utm_medium);
        if (
          utm_Params.utm_campaign !== null &&
          utm_Params.utm_campaign !== undefined
        )
          identifyObj.set("utm_campaign", utm_Params.utm_campaign);
        if (
          utm_Params.utm_content !== null &&
          utm_Params.utm_content !== undefined
        )
          identifyObj.set("utm_content", utm_Params.utm_content);
        if (utm_Params.utm_term !== null && utm_Params.utm_term !== undefined)
          identifyObj.set("utm_term", utm_Params.utm_term);
        amplitude.identify(identifyObj);
      }

      // Set utm_angle as user property and track experiment exposure (web23:
      // not for LP experiment visitors, who skip the bridge split)
      const utmAngle = localStorage.getItem("utm_angle");
      if (utmAngle) {
        const angleIdentify = new Identify();
        angleIdentify.set("utm_angle", utmAngle);
        amplitude.identify(angleIdentify);

        if (isInBridgeSplit(utmAngle)) {
          const showedBridge = shouldShowBridge(utmAngle, bridgeBucketId);
          amplitude.track("ExperimentStarted", {
            experimentName: BRIDGE_EXPERIMENT_NAME,
            variantName: showedBridge ? "treatment" : "control",
            utm_angle: utmAngle,
          });
        }
      }

      amplitude.track("quiz", {
        step: "0",
        step_name: "Start",
        funnel_variation: FunnelVariation.General,
        utm_source: utm_Params?.utm_source,
        utm_medium: utm_Params?.utm_medium,
        utm_campaign: utm_Params?.utm_campaign,
        utm_content: utm_Params?.utm_content,
        utm_term: utm_Params?.utm_term,
      });

      // Fire tracking events for quiz start
      trackCustom("QuizStarted");
      trackQuizStarted();

      console.log("amplitudeDeviceId", amplitudeDeviceId);
      hasFiredEvent.current = true; // Block future executions
    }
  }, [isLoading]); // Dependency on isLoading

  if (isLoading || isInitialFunnelRouting || !languageContext) {
    return <div></div>;
  }

  const currentPageProps = survey.currentPage.props ?? {};
  const showHeader = currentPageProps.showHeader ?? true;
  const showBackButton = currentPageProps.showBackButton ?? true;
  const showCurrentSection = currentPageProps.showCurrentSection ?? true;
  const nextButtonText = pickLoc(currentPageProps.nextButtonText) ?? t("common.continue");
  const nextButtonCaption = pickLoc(currentPageProps.nextButtonCaption);
  const CustomHeader = currentPageProps.customHeader ?? null;
  // QuizHeading values are English enums; show their translation.
  const pageHeading = quizNav.getQuestionHeading(survey.currentPage.name);
  const currentPageHeading = pageHeading ? t(`quiz.headings.${pageHeading}`) : undefined;
  const disclaimer =
    survey.currentPage.name === "email" && emailAccountGate.status === "error"
      ? t("quiz.email.checkError")
      : currentPageProps.disclaimer ?? null;
  const showCaptionSeparator = currentPageProps.showCaptionSeparator ?? true;
  const nextButtonClickValue = currentPageProps.nextButtonClickValue;
  const nextButtonClickEvent = currentPageProps.nextButtonClickEvent;
  // web43 variation entry page: no chevron on the primary, and a secondary
  // "No thanks, just me" that records the skip and opens the guest-pass page.
  const nextButtonHideArrow = currentPageProps.nextButtonHideArrow ?? false;
  const secondaryButtonText = pickLoc(currentPageProps.secondaryButtonText);
  const handleSecondaryClick = secondaryButtonText
    ? () => {
        if (currentPageProps.secondaryButtonClickEvent) {
          amplitude.track(currentPageProps.secondaryButtonClickEvent);
        }
        if (currentPageProps.secondaryButtonClickValue !== undefined) {
          survey.setValue(survey.currentPage.name, currentPageProps.secondaryButtonClickValue);
        }
        const target = currentPageProps.secondaryButtonTargetPage
          ? survey.getPageByName(currentPageProps.secondaryButtonTargetPage)
          : null;
        if (target) {
          survey.currentPage = target;
        } else {
          survey.nextPage();
        }
      }
    : undefined;
  const isEmailAccountCheckLoading =
    survey.currentPage.name === "email" && emailAccountGate.status === "checking";
  const nextButtonLabel = isEmailAccountCheckLoading
    ? t("quiz.email.checking")
    : nextButtonText;

  const hsOffer = localStorage.getItem("hs_offer");
  console.log(survey);
  const signupAccountGateElement = shouldBlockSignupAccountGate ? (
    checkedSignupAccount?.exists || emailAccountGate.status === "found" ? (
      <AccountExistsScreen
        onBack={handleSignupAccountExistsBack}
        source="signup_gate"
      />
    ) : (
      <div></div>
    )
  ) : null;

  return (
    <StatsigProvider client={client}>
      <QuizLanguageContext.Provider value={languageContext}>
      <AppContainer>
        {isQuizRoute && bfSale && bfBannerDesign && (
          <QuizMatchBannerSlot data-testid="bf-sale-quiz-banner">
            <Web48Banner arm={bfBannerDesign} sale={bfSale} />
          </QuizMatchBannerSlot>
        )}
        {isQuizRoute && !bfSale && web46Arm === 'personalization' && (
          <QuizMatchBannerSlot data-testid="web46-quiz-banner">
            <Quiz4UrgencyBanner
              line1={t("results.banner.specialDiscountUnlocked")}
              line2={t("results.banner.percentOffTrial", { percent: 20 })}
              line3={`${t("results.banner.specialDiscountUnlocked")} ${t("results.banner.percentOffTrial", { percent: 20 })}`}
              showTimer={false}
            />
          </QuizMatchBannerSlot>
        )}
        {isQuizRoute && !bfSale && web48Arm && (
          <QuizMatchBannerSlot data-testid="web48-quiz-banner">
            <Web48Banner arm={web48Arm} endsAt={web48EndsAt} />
          </QuizMatchBannerSlot>
        )}
        {!isCheckoutRoute && <ReferralBanner />}
        <DevTool survey={survey} />
        <Routes>
          <Route
            path="/page/:pageNumber"
            element={
              emailAccountGate.status === "found" ? (
                <AccountExistsScreen
                  onBack={handleEmailAccountExistsBack}
                  source="email_step"
                />
              ) : (
                <>
              {hsOffer === 'potato' ? (
                <Project60StickyBanner
                  line1="SPECIAL BONUS"
                  line2="30-Day CPB Diets Included Free With Your Personalized Plan"
                  showTimer={false}
                />
              ) : null}
                {progressTitle && TransitionComponent !== LandingPage ? (
                  <>
                    <BlackFridayTimer mini />
                  </>
                ) : null}
                {TransitionComponent == null ? (
                  <>
                    {CustomHeader && <CustomHeader />}
                    {showHeader && (
                      <HeaderContainer>
                        <ProgressHeader
                          title={progressTitle}
                          progress={progressPercent}
                        />
                      </HeaderContainer>
                    )}
                    <QuestionHeader
                      user={user}
                      survey={survey}
                      showBackButton={showBackButton}
                      showCurrentSection={showCurrentSection}
                      sectionName={currentPageHeading}
                    />
                    <Survey className="kokolo" model={survey} />
                    <QuestionFooter
                      survey={survey}
                      nextButtonText={nextButtonLabel}
                      nextButtonCaption={nextButtonCaption}
                      nextButtonClickValue={nextButtonClickValue}
                      disclaimer={disclaimer}
                      showCaptionSeparator={showCaptionSeparator}
                      nextButtonClickEvent={nextButtonClickEvent}
                      isLoading={isEmailAccountCheckLoading}
                      nextButtonHideArrow={nextButtonHideArrow}
                      secondaryButtonText={secondaryButtonText}
                      onSecondaryClick={handleSecondaryClick}
                    />
                  </>
                ) : (
                  <TransitionComponent
                    survey={survey}
                    user={user}
                    onComplete={onComplete}
                    openingArcOn={openingArcOn}
                  />
                )}
              </>
              )
            }
          />
          <Route
            path="/bridge"
            element={
              <BridgeScreen
                onContinue={() => navigate("/page/1")}
                onAnswer={(name, value) => {
                  // Capture a Merged Question's answer into the survey so the
                  // quiz has it. (Skipping the question's own page later in the
                  // flow is the separate Intro Sequence work.)
                  survey.setValue(name, value);
                }}
              />
            }
          />
         <Route
  path="/results"
  element={signupAccountGateElement ?? (
    <>
      {hsOffer === 'potato' ? (
        <Project60StickyBanner
          line1="SPECIAL BONUS"
          line2="30-Day CPB Diets Included Free With Your Personalized Plan"
          showTimer={false}
        />
      ) : null}
      <LandingPage user={user} onComplete={onComplete} survey={survey} client={client} isClientLoading={isLoading} />
    </>
  )}
/>
          <Route
            path="/checkout"
            element={signupAccountGateElement ?? (
              <>
                {hsOffer === 'potato' ? (
                  <Project60StickyBanner
                    line1="SPECIAL BONUS"
                    line2="30-Day CPB Diets Included Free With Your Personalized Plan"
                    showTimer={false}
                  />
                ) : null}
                <CheckoutPage user={user} onComplete={onComplete} survey={survey} /> 
              </>
            )}
          />
          {/* web41_start_date_reservation: fake-door reservation flow */}
          <Route path="/reserve" element={<ReservationPage user={user} />} />
          <Route path="/reserve/soon" element={<ReservationComingSoon user={user} />} />
        </Routes>
      </AppContainer>
      </QuizLanguageContext.Provider>
    </StatsigProvider>
  );
}

export default App;
